import React, { useMemo } from "react";
import ActivityLoader from "../Common/ActivityLoader";
import Notification from "../Common/Notification";

const safe = (v) => (v === null || v === undefined || String(v).trim() === "" ? "—" : v);

const pickIcon = (account) => {
  const cat = String(account?.account_category_id ?? "");
  // You can refine these rules later; for now keep it subtle & consistent
  if (cat === "3") return { icon: "fa fa-desktop", cls: "yx-vaIcon--indigo" };
  return { icon: "fa fa-briefcase", cls: "yx-vaIcon--muted" };
};

const VendorAccounts = ({ accounts, handleStatement }) => {
  const list = useMemo(() => (Array.isArray(accounts) ? accounts : []), [accounts]);

  const isLoading = !accounts;
  const is404 = accounts === "404";

  return (
    <>
      <div className="yx-card yx-vaCar mb-4">
        {/* Head */}
        <div className="yx-card-head yx-vaHead">
          <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
            <span className="yx-vaHeadIcon">
              <i className="fa fa-university" />
            </span>

            <div style={{ minWidth: 0 }}>
              <div className="yx-title">Accounts</div>
              <div className="yx-sub">Quick access statements</div>
            </div>
          </div>

          <span className="yx-vaCount">{isLoading ? "…" : list.length}</span>
        </div>

        {/* Body */}
        <div className="yx-card-body yx-vaBody">
          {isLoading && (
            <div className="py-2">
              <ActivityLoader />
            </div>
          )}

          {is404 && <Notification message="No account record found" closable={false} />}

          {!isLoading && !is404 && list.length === 0 && (
            <div className="yx-muted" style={{ fontSize: 13, fontWeight: 700 }}>
              <i className="fa fa-info-circle mr-2" />
              No accounts linked to this vendor yet.
            </div>
          )}

          {!isLoading && !is404 && list.length > 0 && (
            <>
              <div className="yx-vaRail overflow-auto horizontal-scroll-container">
                {list.map((a, idx) => {
                  const { icon, cls } = pickIcon(a);

                  return (
                    <button
                      key={a?.id ?? idx}
                      type="button"
                      className="yx-vaItem"
                      onClick={() => handleStatement(a)}
                      title="Open statement"
                    >
                      <span className={`yx-vaIcon ${cls}`}>
                        <i className={icon} />
                      </span>

                      <span className="yx-vaText">
                        <span className="yx-vaNo">{safe(a.account_number)}</span>
                        <span className="yx-vaName" title={safe(a.account_name)}>
                          {safe(a.account_name)}
                        </span>
                      </span>

                      <span className="yx-vaAction" aria-hidden="true">
                        <i className="fa fa-file-lines" />
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="yx-vaHint">
                <i className="fa fa-arrows-left-right mr-2" />
                Scroll sideways to view more
              </div>
            </>
          )}
        </div>
      </div>

      {/* Put this CSS into your global css file */}
      <style>{css}</style>
    </>
  );
};

const css = `
/* ---------- Vendor Accounts card ---------- */
.yx-vaHead{
  display:flex;
  align-items:center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  padding-bottom: 12px;
}

.yx-vaHeadIcon{
  width: 34px;
  height: 34px;
  border-radius: 14px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(79,70,229,.08);
  border: 1px solid rgba(79,70,229,.14);
  color: var(--yx-accent, #4F46E5);
  flex: 0 0 auto;
}

.yx-vaCount{
  height: 30px;
  min-width: 34px;
  padding: 0 10px;
  border-radius: 12px;
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(15,23,42,.02);
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight: 900;
  font-size: 12.5px;
  color: rgba(30,41,59,.92);
  flex: 0 0 auto;
}

.yx-vaBody{
  padding-top: 12px;
  padding-bottom: 12px;
  flex: 1 1 auto;
  display:flex;
  flex-direction: column;
  min-height: 0; /* allow rail to scroll inside fixed height */
  
}

.yx-vaRail{
  display:flex;
  gap: 12px;
  padding-bottom: 6px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  max-width: 65vw;
}

.yx-vaItem{
  scroll-snap-align: start;
  width: 280px;
  min-width: 280px;
  height: 66px;                 /* compact chip-card */
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.07);
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
  display:flex;
  align-items:center;
  gap: 12px;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
}

.yx-vaItem:hover{
  border-color: rgba(79,70,229,.18);
  box-shadow: 0 6px 16px rgba(15,23,42,.06);
}

.yx-vaIcon{
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(15,23,42,.03);
  border: 1px solid rgba(15,23,42,.07);
  color: rgba(71,85,105,.95);
  flex: 0 0 auto;
}

.yx-vaIcon--indigo{
  background: rgba(79,70,229,.10);
  border-color: rgba(79,70,229,.18);
  color: var(--yx-accent, #4F46E5);
}

.yx-vaIcon--muted{
  background: rgba(15,23,42,.03);
  border-color: rgba(15,23,42,.07);
  color: rgba(71,85,105,.95);
}

.yx-vaText{
  min-width: 0;
  flex: 1 1 auto;
  display:flex;
  flex-direction: column;
  gap: 4px;
}

.yx-vaNo{
  font-size: var(--yx-row, 13px);
  font-weight: 950;
  color: rgba(30,41,59,.92);
  line-height: 1.1;
}

.yx-vaName{
  font-size: 12.5px;
  font-weight: 800;
  color: rgba(100,116,139,.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.yx-vaAction{
  width: 42px;
  height: 42px;
  border-radius: 16px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(79,70,229,.10);
  border: 1px solid rgba(79,70,229,.16);
  color: var(--yx-accent, #4F46E5);
  flex: 0 0 auto;
}

.yx-vaHint{
  margin-top: auto;
  font-size: 11.5px;
  font-weight: 800;
  color: rgba(100,116,139,.92);
  display:flex;
  align-items:center;
  gap: 6px;
}
`;

export default VendorAccounts;
